import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationApi } from '../api';
import { Avatar } from './ui';
import ToastStack from './ToastStack';
import SuspendedScreen from './SuspendedScreen';
import SubscriptionWarningBanner from './SubscriptionWarningBanner';
import InstallAppBanner from './InstallAppBanner';
import { useReminderAlerts } from '../hooks/useReminderAlerts';

const SHOWN_NOTIF_KEY = 'tracern:shownNotifIds';
const TOAST_TTL_MS = 12000;

function loadShownIds() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SHOWN_NOTIF_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function saveShownIds(set) {
  const arr = [...set].slice(-100);
  sessionStorage.setItem(SHOWN_NOTIF_KEY, JSON.stringify(arr));
}

// ── Nav config ────────────────────────────────────────────────────────────────
const NAV = {
  SUPER_ADMIN: [
    { path: '/dashboard/super-admin', icon: '⊞', label: 'Home'    },
    { path: '/tasks',                 icon: '✓', label: 'Tasks'   },
    { path: '/pool',                  icon: '⊕', label: 'Pool'    },
    { path: '/notifications',         icon: '🔔', label: 'Alerts' },
    { path: '/notepad',               icon: '📝', label: 'Pad'    },
  ],
  ADMIN: [
    { path: '/dashboard/admin', icon: '⊞', label: 'Home'    },
    { path: '/tasks',           icon: '✓', label: 'Tasks'   },
    { path: '/employees',       icon: '👤', label: 'People' },
    { path: '/notifications',   icon: '🔔', label: 'Alerts' },
    { path: '/notepad',         icon: '📝', label: 'Pad'    },
  ],
  EMPLOYEE: [
    { path: '/dashboard/employee', icon: '⊞', label: 'Home'  },
    { path: '/tasks',              icon: '✓', label: 'Tasks' },
    { path: '/pool',               icon: '⊕', label: 'Pool'  },
    { path: '/notifications',      icon: '🔔', label: 'Alerts'},
    { path: '/notepad',            icon: '📝', label: 'Pad'  },
  ],
};

const SIDEBAR = {
  SUPER_ADMIN: [
    { section: 'Overview' },
    { path: '/dashboard/super-admin', icon: '⊞', label: 'Dashboard'         },
    { path: '/tasks/critical',        icon: '🚨', label: 'Critical'          },
    { path: '/tasks/overdue',         icon: '⏰', label: 'Overdue'           },
    { section: 'Work' },
    { path: '/tasks',               icon: '✓', label: 'All Tasks'  },
    { path: '/tasks/create',        icon: '＋', label: 'New Task'   },
    { path: '/pool',                icon: '⊕', label: 'Task Pool'  },
    { section: 'People' },
    { path: '/employees',           icon: '👤', label: 'Employees'          },
    { path: '/deletion-approvals',  icon: '🗂', label: 'Deletion Approvals' },
    { path: '/departments',         icon: '🏢', label: 'Departments'        },
    { section: 'Insights' },
    { path: '/reports',             icon: '📊', label: 'Reports'        },
    { path: '/logs/activity',       icon: '📋', label: 'Activity Log'   },
    { path: '/logs/admin-actions',  icon: '🔍', label: 'Admin Actions'  },
    { path: '/logs/security',       icon: '🔐', label: 'Security Audit' },
    { section: 'My Space' },
    { path: '/notepad',         icon: '📝', label: 'My Notepad'      },
    { path: '/reminders',       icon: '⏰', label: 'Reminders'        },
    { path: '/my-account',      icon: '👤', label: 'My Account'    },
    { section: 'Company' },
    { path: '/holidays',        icon: '🏖', label: 'Holidays'         },
    { path: '/announcements',   icon: '📢', label: 'Announcements'    },
    { path: '/tasks/bulk',      icon: '📤', label: 'Bulk Task Upload' },
    { section: 'System' },
    { path: '/settings',      icon: '⚙', label: 'Settings'     },
    { path: '/dev-tools',     icon: '🔧', label: 'Dev Tools'    },
    { path: '/notifications', icon: '🔔', label: 'Notifications'},
  ],
  ADMIN: [
    { section: 'Overview' },
    { path: '/dashboard/admin', icon: '⊞', label: 'Dashboard' },
    { section: 'Work' },
    { path: '/tasks',      icon: '✓', label: 'Tasks'    },
    { path: '/pool',       icon: '⊕', label: 'Task Pool'},
    { section: 'People' },
    { path: '/employees',  icon: '👤', label: 'Employees' },
    { section: 'Insights' },
    { path: '/reports',        icon: '📊', label: 'Reports'      },
    { path: '/logs/activity',  icon: '📋', label: 'Activity Log' },
    { path: '/logs/security',  icon: '🔐', label: 'Security Audit'},
    { section: 'My Space' },
    { path: '/notepad',       icon: '📝', label: 'My Notepad'      },
    { path: '/reminders',     icon: '⏰', label: 'Reminders'        },
    { path: '/my-account',      icon: '👤', label: 'My Account'    },
    { section: 'Company' },
    { path: '/holidays',      icon: '🏖', label: 'Holidays'         },
    { path: '/announcements', icon: '📢', label: 'Announcements'    },
    { path: '/tasks/bulk',    icon: '📤', label: 'Bulk Task Upload' },
    { section: 'Alerts' },
    { path: '/notifications', icon: '🔔', label: 'Notifications' },
  ],
  EMPLOYEE: [
    { section: 'My Work' },
    { path: '/dashboard/employee', icon: '⊞', label: 'Home'     },
    { path: '/tasks',              icon: '✓', label: 'My Tasks' },
    { path: '/pool',               icon: '⊕', label: 'Task Pool'},
    { path: '/team',               icon: '👥', label: 'My Team' },
    { section: 'My Space' },
    { path: '/notepad',           icon: '📝', label: 'My Notepad'   },
    { path: '/reminders',         icon: '⏰', label: 'Reminders'     },
    { path: '/my-account',      icon: '👤', label: 'My Account'    },
    { section: 'Company' },
    { path: '/holidays',          icon: '🏖', label: 'Holidays'      },
    { path: '/announcements',     icon: '📢', label: 'Announcements' },
    { path: '/tasks/bulk',        icon: '📤', label: 'Bulk Upload'   },
    { section: 'Alerts' },
    { path: '/notifications', icon: '🔔', label: 'Notifications' },
  ],
};

function isActive(itemPath, currentPath) {
  const exact = ['/dashboard/super-admin', '/dashboard/admin', '/dashboard/employee'];
  if (exact.includes(itemPath)) return currentPath === itemPath;
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
}

// ── Sidebar nav item ──────────────────────────────────────────────────────────
function SideNavItem({ item, active, onClick, unread }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        width: '100%', padding: '9px 12px',
        borderRadius: 'var(--radius-md)',
        border: 'none', fontSize: '14px', cursor: 'pointer',
        textAlign: 'left', marginBottom: '1px',
        background: active ? 'var(--color-primary-lighter)' : 'transparent',
        color:      active ? 'var(--color-primary)'         : 'var(--color-text-secondary)',
        fontWeight: active ? 600 : 400,
        transition: 'all var(--t-fast)',
        position: 'relative',
      }}
    >
      <span style={{ fontSize: '16px', width: '20px', textAlign: 'center', flexShrink: 0, lineHeight: 1 }}>{item.icon}</span>
      <span style={{ flex: 1, letterSpacing: '-0.01em' }}>{item.label}</span>
      {item.path === '/notifications' && unread > 0 && (
        <span style={{
          background: 'var(--color-danger)', color: '#fff',
          borderRadius: 'var(--radius-full)', fontSize: '10px',
          fontWeight: 700, padding: '1px 6px', lineHeight: '16px',
        }}>{unread > 9 ? '9+' : unread}</span>
      )}
    </button>
  );
}

// ── Main shell ────────────────────────────────────────────────────────────────
export default function AppShell() {
  const { user, logout }    = useAuth();
  const navigate            = useNavigate();
  const location            = useLocation();
  const [unread, setUnread] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  const [moreOpen, setMoreOpen] = useState(false);
  const shownNotifRef = useRef(loadShownIds());

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const pushToast = useCallback((toast) => {
    setToasts(prev => [...prev, toast]);
    setTimeout(() => dismissToast(toast.id), TOAST_TTL_MS);
  }, [dismissToast]);

  const showReminderAlert = useCallback((reminder) => {
    const message = reminder.note
      ? `${reminder.title} — ${reminder.note}`
      : reminder.title;
    pushToast({
      id: `reminder-${reminder.id}`,
      title: '⏰ Reminder',
      message,
    });
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        new Notification('Reminder', { body: message });
      } catch { /* ignore */ }
    }
  }, [pushToast]);

  useReminderAlerts(showReminderAlert);

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const poll = () => {
      Promise.all([
        notificationApi.getUnreadCount(),
        notificationApi.getAll(),
      ])
        .then(([countRes, listRes]) => {
          setUnread(countRes.data?.data?.count || 0);
          const notifications = listRes.data?.data || [];
          notifications
            .filter(n => !n.read && n.type === 'REMINDER')
            .forEach(n => {
              const key = String(n.id);
              if (shownNotifRef.current.has(key)) return;
              shownNotifRef.current.add(key);
              saveShownIds(shownNotifRef.current);
              pushToast({
                id: `notif-${key}`,
                title: '⏰ Reminder',
                message: n.message,
              });
            });
        })
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => clearInterval(id);
  }, [location.pathname, pushToast]);

  const role         = user?.role || 'EMPLOYEE';
  const navItems     = NAV[role]     || NAV.EMPLOYEE;
  const sidebarItems = SIDEBAR[role] || SIDEBAR.EMPLOYEE;
  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (user?.__suspended) return <SuspendedScreen message={user.message} />;

  return (
    <div style={{ display: 'flex', minHeight: '100dvh', background: 'var(--color-bg)' }}>
      <ToastStack toasts={toasts} onDismiss={dismissToast} />

      {/* ── Sidebar (desktop) ──────────────────────────────────────────────── */}
      {!mobile && (
        <aside style={{
          width: '240px', background: 'var(--color-surface)',
          borderRight: '1px solid var(--color-border)',
          display: 'flex', flexDirection: 'column',
          position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 'var(--z-nav)',
        }}>
          {/* Logo */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '20px 20px 18px',
            borderBottom: '1px solid var(--color-border)',
          }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: 'var(--radius-md)',
              background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)',
              color: '#fff', fontSize: '18px', fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-brand)',
              boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
            }}>T</div>
            <div>
              <p style={{ fontSize: '18px', fontWeight: 800, color: 'var(--color-text)', letterSpacing: '-0.04em', fontFamily: 'var(--font-brand)', lineHeight: 1.1 }}>Tracern</p>
              <p style={{ fontSize: '9px', color: 'var(--color-text-faint)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>by Mahashunya</p>
            </div>
          </div>

          {/* Nav */}
          <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
            {sidebarItems.map((item, i) =>
              item.section ? (
                <p key={i} style={{
                  fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'var(--color-text-faint)',
                  padding: '14px 12px 6px',
                }}>{item.section}</p>
              ) : (
                <SideNavItem
                  key={item.path}
                  item={item}
                  active={isActive(item.path, location.pathname)}
                  onClick={() => {
                    if (item.path === '__more__') { setMoreOpen(o => !o); return; }
                    navigate(item.path);
                    if (item.path === '/notifications') setUnread(0);
                    setMoreOpen(false);
                  }}
                  unread={unread}
                />
              )
            )}
          </nav>

          {/* Footer */}
          <div style={{ borderTop: '1px solid var(--color-border)', padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <Avatar name={user?.name || '?'} size={34} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>{user?.name}</p>
                <p style={{ fontSize: '11px', color: 'var(--color-text-faint)' }}>{user?.employeeId}</p>
              </div>
            </div>
            <button onClick={handleLogout} style={{
              width: '100%', padding: '8px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-bg)', color: 'var(--color-text-muted)',
              fontSize: '13px', border: '1px solid var(--color-border)',
              cursor: 'pointer', fontWeight: 500,
              transition: 'all var(--t-fast)',
            }}>Sign out</button>
            <p style={{ fontSize: '10px', color: 'var(--color-text-faint)', textAlign: 'center', marginTop: '10px', lineHeight: 1.5 }}>
              © {new Date().getFullYear()} Mahashunya Innovation Labs
            </p>
          </div>
        </aside>
      )}

      {/* ── Main ──────────────────────────────────────────────────────────── */}
      <main style={{
        flex: 1,
        marginLeft: mobile ? 0 : '240px',
        display: 'flex', flexDirection: 'column',
        minHeight: '100dvh', minWidth: 0,
      }}>

        {/* Mobile top bar */}
        {mobile && (
          <header style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 16px', height: '56px',
            background: 'var(--color-surface)',
            borderBottom: '1px solid var(--color-border)',
            position: 'sticky', top: 0, zIndex: 'var(--z-sticky)',
            boxShadow: 'var(--shadow-xs)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: 'var(--radius-sm)',
                background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)',
                color: '#fff', fontSize: '16px', fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-brand)',
              }}>T</div>
              <div>
                <p style={{ fontWeight: 800, fontSize: '16px', color: 'var(--color-text)', letterSpacing: '-0.04em', fontFamily: 'var(--font-brand)', lineHeight: 1.1 }}>Tracern</p>
                <p style={{ fontSize: '9px', color: 'var(--color-text-faint)', letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1 }}>by Mahashunya</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {unread > 0 && (
                <button onClick={() => navigate('/notifications')} style={{
                  position: 'relative', background: 'none', border: 'none',
                  padding: '4px', cursor: 'pointer', fontSize: '20px',
                }}>
                  🔔
                  <span style={{
                    position: 'absolute', top: '0', right: '0',
                    background: 'var(--color-danger)', color: '#fff',
                    borderRadius: 'var(--radius-full)', fontSize: '9px',
                    fontWeight: 700, padding: '1px 4px', lineHeight: '14px',
                    minWidth: '14px', textAlign: 'center',
                  }}>{unread > 9 ? '9+' : unread}</span>
                </button>
              )}
              <Avatar name={user?.name || '?'} size={32} />
            </div>
          </header>
        )}

        {/* Subscription banner */}
        {user?.subscriptionWarning && (
          <SubscriptionWarningBanner
            message={user.subscriptionWarning}
            daysLeft={user.subscriptionDaysLeft}
          />
        )}

        <InstallAppBanner />

        {/* Page content */}
        <div style={{
          flex: 1,
          paddingBottom: mobile ? '76px' : '0',
          overflowX: 'hidden',
        }}>
          <Outlet />
        </div>

        {/* Mobile bottom nav */}
        {mobile && (
          <nav style={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            height: '64px',
            background: 'var(--color-surface)',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            zIndex: 'var(--z-nav)',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
          }}>
            {navItems.map(item => {
              const active = item.path !== '__more__' && isActive(item.path, location.pathname);
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    if (item.path === '__more__') { setMoreOpen(o => !o); return; }
                    navigate(item.path);
                    if (item.path === '/notifications') setUnread(0);
                    setMoreOpen(false);
                  }}
                  style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '6px 4px 10px',
                    color: active ? 'var(--color-primary)' : 'var(--color-text-faint)',
                    position: 'relative',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {/* Active indicator dot */}
                  {active && (
                    <span style={{
                      position: 'absolute', top: '6px',
                      width: '4px', height: '4px', borderRadius: '50%',
                      background: 'var(--color-primary)',
                    }} />
                  )}
                  <div style={{ position: 'relative' }}>
                    <span style={{ fontSize: '22px', lineHeight: 1, display: 'block' }}>{item.icon}</span>
                    {item.path === '/notifications' && unread > 0 && (
                      <span style={{
                        position: 'absolute', top: '-3px', right: '-5px',
                        background: 'var(--color-danger)', color: '#fff',
                        borderRadius: 'var(--radius-full)', fontSize: '8px',
                        fontWeight: 700, padding: '0 4px', lineHeight: '14px',
                        minWidth: '14px', textAlign: 'center',
                      }}>{unread > 9 ? '9+' : unread}</span>
                    )}
                  </div>
                  <span style={{
                    fontSize: '10px', fontWeight: active ? 700 : 400,
                    marginTop: '3px', letterSpacing: '-0.01em',
                  }}>{item.label}</span>
                </button>
              );
            })}
          </nav>
        )}
      </main>
    </div>
  );
}
